import { useState, useRef, useEffect } from 'react';

/**
 * OTP input a 6 cifre con auto-advance e auto-submit.
 *
 * Props:
 *  - length: numero di cifre (default 6)
 *  - onComplete(code): callback quando tutte le cifre sono inserite
 *  - disabled: disabilita l'input
 *  - error: messaggio di errore da mostrare
 *  - autoFocus: focus automatico sul primo input (default true)
 */
export default function MFAInput({ length = 6, onComplete, disabled = false, error = '', autoFocus = true }) {
  const [values, setValues] = useState(Array(length).fill(''));
  const inputsRef = useRef([]);

  useEffect(() => {
    if (autoFocus && inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    if (error) {
      setValues(Array(length).fill(''));
      if (inputsRef.current[0]) inputsRef.current[0].focus();
    }
  }, [error, length]);

  const handleChange = (index, e) => {
    const val = e.target.value;
    if (val && !/^\d$/.test(val)) return;

    const next = [...values];
    next[index] = val;
    setValues(next);

    if (val && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }

    if (val && index === length - 1) {
      const code = next.join('');
      if (code.length === length) {
        onComplete?.(code);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !values[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    const next = Array(length).fill('');
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted[i];
    }
    setValues(next);
    if (pasted.length === length) {
      onComplete?.(pasted);
    } else {
      inputsRef.current[pasted.length]?.focus();
    }
  };

  return (
    <div>
      <div className="flex justify-center gap-2">
        {values.map((v, i) => (
          <input
            key={i}
            ref={el => inputsRef.current[i] = el}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={v}
            onChange={e => handleChange(i, e)}
            onKeyDown={e => handleKeyDown(i, e)}
            onPaste={i === 0 ? handlePaste : undefined}
            disabled={disabled}
            className={`w-11 h-13 text-center text-xl font-semibold border-2 rounded-lg
              focus:outline-none focus:ring-2 transition-colors
              ${error
                ? 'border-red-400 focus:ring-red-300 focus:border-red-500'
                : 'border-gray-300 focus:ring-blue-300 focus:border-blue-500'}
              ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
            `}
            aria-label={`Digit ${i + 1}`}
          />
        ))}
      </div>
      {error && (
        <p className="text-sm text-red-600 text-center mt-2">{error}</p>
      )}
    </div>
  );
}
